/* eslint-disable react-refresh/only-export-components */
import { lazy } from 'react';

import { Navigate, type RouteObject } from 'react-router-dom';

import PrizePage from '@/pages/prize-page/ui';

import { GuardDevPages } from './GuardDevPages';
import { Outlet } from './Outlet';
import { RouterErrorPage } from './RouterErrorPage';
import { ROUTE_PATHS } from './types';

/**
 * Use ‘lazy’ from react instead of ‘RouteObject.lazy’
 * so you don't have to wait for scripts to load before navigating.
 * Otherwise Suspense doesn't work properly and fallback is never displayed.
 * */
const CatalogPage = lazy(() => import('@/pages/catalog-page/ui/CatalogPage'));
const CatalogPageV2 = lazy(() => import('@/pages/catalog-page-v2/ui/CatalogPage'));
const FeedPage = lazy(() => import('@/pages/feed-page/ui/FeedPage'));
const DesktopPage = lazy(() => import('@/pages/desktop-page/ui/DesktopPage'));
const DesktopQrPage = lazy(() => import('@/pages/desktop-qr-page/ui/DesktopQrPage'));
const GenresPage = lazy(() => import('@/pages/genres-page/ui/GenresPage'));
const GamesByCategoryPage = lazy(
  () => import('@/pages/games-by-category-page/ui/GamesByCategoryPage')
);
const GamePage = lazy(() => import('@/pages/game-page/ui/GamePage'));

const ProfilePage = lazy(() => import('@/pages/profile-page/ui/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/settings-page/ui/SettingsPage'));

const SearchPage = lazy(() => import('@/pages/search-page/ui/SearchPage'));
const GameRequestPage = lazy(
  () => import('@/pages/game-request-page/ui/GameRequestPage')
);

const TasksPage = lazy(() => import('@/pages/tasks-page/ui'));
const LootboxPage = lazy(() => import('@/pages/lootbox-page/ui/LootboxPage'));
const PollPage = lazy(() => import('@/pages/poll-page/ui/PollPage'));

const ShopPage = lazy(() => import('@/pages/shop-page/ui/ShopPage'));
const WalletPage = lazy(() => import('@/pages/wallet-page/ui/WalletPage'));

const LeaderboardPage = lazy(() => import('@/pages/leaderboard-page/ui'));

const ClickerPage = lazy(() => import('@/pages/clicker-page/ui'));

const TermsConditionsPage = lazy(
  () => import('@/pages/term-conditions-page/ui/TermsConditionsPage')
);

const DevPage = lazy(() => import('@/pages/dev-pages/dev-page/ui/DevPage'));
const StorybookPage = lazy(
  () => import('@/pages/dev-pages/storybook-page/ui/StoryBookPage')
);

const OnboardingPage = lazy(() => import('@/pages/onboarding/ui/OnboardingPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found-page/ui/NotFoundPage'));

const DaysStreakPage = lazy(() => import('@/pages/days-streak-page/ui/DaysStreakPage'));

const ListingPage = lazy(() => import('@/pages/listing-page/ui/ListingPage'));

const DEV_PAGES_CONFIG: RouteObject[] = [
  {
    path: ROUTE_PATHS.DEV,
    element: <GuardDevPages />,
    children: [
      {
        index: true,
        element: <DevPage />,
      },
      {
        path: ROUTE_PATHS.STORYBOOK,
        element: <StorybookPage />,
      },
    ],
  },
  {
    path: `${ROUTE_PATHS.CATEGORY}/sandbox`,
    element: <GamesByCategoryPage />,
  },
];

export const ROUTES_CONFIG: RouteObject[] = [
  {
    path: ROUTE_PATHS.MAIN,
    element: <Outlet />,
    errorElement: import.meta.env.DEV ? undefined : <RouterErrorPage />,
    children: [
      {
        index: true,
        element: <CatalogPageV2 />,
      },
      {
        path: '/catalog-old',
        element: <CatalogPage />,
      },
      {
        path: ROUTE_PATHS.CATEGORY,
        children: [
          {
            index: true,
            element: <GenresPage />,
          },
          {
            path: ':categoryId',
            element: <GamesByCategoryPage />,
          },
        ],
      },
      { path: ROUTE_PATHS.GAMES, element: <Navigate to="/" replace /> },
      {
        path: `${ROUTE_PATHS.GAMES}/:gameKey`,
        element: <GamePage />,
      },
      {
        path: ROUTE_PATHS.PROFILE,
        element: <ProfilePage />,
      },
      {
        path: ROUTE_PATHS.SETTINGS,
        element: <SettingsPage />,
      },
      {
        path: ROUTE_PATHS.SEARCH,
        element: <SearchPage />,
      },
      {
        path: ROUTE_PATHS.GAME_REQUEST,
        element: <GameRequestPage />,
      },
      {
        path: ROUTE_PATHS.TASKS,
        element: <TasksPage />,
      },
      {
        path: ROUTE_PATHS.POLL,
        element: <PollPage />,
      },
      {
        // keep wheel route because there are external links to it
        path: ROUTE_PATHS.WHEEL,
        element: <TasksPage />,
      },
      {
        path: `/:parentPath?/${ROUTE_PATHS.PRIZE}`,
        element: <PrizePage />,
      },
      {
        path: `/:parentPath?/:parentPath?/${ROUTE_PATHS.PRIZE}`,
        element: <PrizePage />,
      },
      {
        path: `/:parentPath?/${ROUTE_PATHS.LOOTBOX}/:userLootboxId`,
        element: <LootboxPage />,
      },
      {
        path: ROUTE_PATHS.FEED,
        element: <FeedPage />,
      },
      {
        path: ROUTE_PATHS.SHOP,
        element: <ShopPage />,
      },
      {
        path: ROUTE_PATHS.WALLET,
        element: <WalletPage />,
      },
      {
        path: ROUTE_PATHS.LEADERBOARDS,
        element: <Navigate to={`${ROUTE_PATHS.PROFILE}?tab=friends`} replace />,
      },
      {
        path: `${ROUTE_PATHS.LEADERBOARDS}/:leaderboardId`,
        element: <LeaderboardPage />,
      },
      {
        path: `/:parentPath?/:parentPath?/${ROUTE_PATHS.CLICKER}`,
        element: <ClickerPage />,
      },
      {
        path: ROUTE_PATHS.DAYS_STREAK,
        element: <DaysStreakPage />,
      },
      {
        path: ROUTE_PATHS.LISTING,
        element: <ListingPage />,
      },
      {
        path: ROUTE_PATHS.TERMS_CONDITIONS,
        element: <TermsConditionsPage />,
      },
      ...DEV_PAGES_CONFIG,
      {
        path: ROUTE_PATHS.ONBOARDING,
        element: <OnboardingPage />,
      },
    ],
  },
  {
    path: ROUTE_PATHS.NOT_FOUND,
    element: <NotFoundPage />,
  },
];

export const DESKTOP_ROUTES_CONFIG: RouteObject[] = [
  {
    path: ROUTE_PATHS.MAIN,
    element: <Outlet hideNavbar />,
    errorElement: import.meta.env.DEV ? undefined : <RouterErrorPage />,
    children: [
      {
        index: true,
        element: <DesktopPage />,
      },
      {
        path: ROUTE_PATHS.DESKTOP_QR,
        element: <DesktopQrPage />,
      },
      {
        path: ROUTE_PATHS.NOT_FOUND,
        element: <NotFoundPage />,
      },
      ...DEV_PAGES_CONFIG,
    ],
  },
];
